import {GRAPH_DATA} from 'src/app/_interfaces/orderDetail.interface'
import {SalesByChannelItem, TopStockedItem} from '../types/dashboard.types'
import {sumOrdersFromGraphData} from './orderSummary'

export type NormalizedSalesRow = {
  platformId?: number
  channel: string
  amount: number
  currency: string
}

export type NormalizedStockRow = {
  name: string
  sku: string
  quantity: number
}

const toNumber = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const pickArray = (...candidates: unknown[]): unknown[] => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate
    }
  }
  return []
}

export const parseSalesByChannel = (
  payloadData: Record<string, unknown> = {}
): NormalizedSalesRow[] => {
  const raw = pickArray(
    payloadData.sales_by_channel,
    payloadData.channel_sales,
    payloadData.sales_by_platform,
    payloadData.platform_sales,
    payloadData.sales_channels,
    payloadData.channels_sales,
    payloadData.channels
  )

  if (raw.length > 0) {
    return (raw as SalesByChannelItem[]).map((row) => ({
      platformId: row.platform_id,
      channel: row.display_name || row.channel || row.platform_name || 'Unknown',
      amount: toNumber(row.amount ?? row.total_value ?? row.value),
      currency: row.currency_iso || row.currency || 'USD',
    }))
  }

  const objectCandidate =
    payloadData.sales_by_channel ||
    payloadData.channel_sales ||
    payloadData.sales_by_platform

  if (objectCandidate && typeof objectCandidate === 'object' && !Array.isArray(objectCandidate)) {
    return Object.entries(objectCandidate as Record<string, unknown>).map(([channel, amount]) => ({
      channel,
      amount: toNumber(amount),
      currency: 'USD',
    }))
  }

  return []
}

export const parseTopStockedItems = (payloadData: Record<string, unknown> = {}): NormalizedStockRow[] => {
  const raw = pickArray(
    payloadData.top_stocked_items,
    payloadData.top_stock_items,
    payloadData.top_items,
    payloadData.most_stocked_items,
    payloadData.inventory_top_items
  )

  return (raw as TopStockedItem[]).map((row) => ({
    name: row.name || row.title || 'Unknown item',
    sku: row.sku || '—',
    quantity: toNumber(row.quantity ?? row.available_qty ?? row.qty),
  }))
}

export const sumOrderValueFromGraphData = (graphData: GRAPH_DATA[]): number => {
  return (graphData || []).reduce((sum, item) => {
    if (!item?.orders) return sum
    const orders = item.orders as Record<string, number | undefined>
    const value = orders.total_value ?? orders.order_value ?? orders.value ?? 0
    return sum + toNumber(value)
  }, 0)
}

export const sumOrderQuantityFromGraphData = (graphData: GRAPH_DATA[]): number =>
  sumOrdersFromGraphData(graphData)

export const parseListingProductRows = (rows: Record<string, unknown>[] = []): NormalizedStockRow[] =>
  rows.map((row) => ({
    name: String(row.name || row.product_name || row.title || 'Unknown item'),
    sku: String(row.sku || row.offer_sku || '—'),
    quantity: toNumber(
      row.total_available_quantity ?? row.available_quantity ?? row.quantity ?? row.qty
    ),
  }))

export const formatDashboardCurrency = (
  value: number,
  currencyIso = 'USD',
  decimals = 2,
  currencySymbol?: string
): string => {
  try {
    if (currencySymbol) {
      const formattedNum = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value)
      return `${currencySymbol}${formattedNum}`
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyIso,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value)
  } catch {
    if (currencySymbol) return `${currencySymbol}${value.toFixed(decimals)}`
    return `${currencyIso} ${value.toFixed(decimals)}`
  }
}

export const getCurrencySymbol = (currencyIso = 'USD'): string => {
  try {
    const parts = new Intl.NumberFormat('en-US', {style: 'currency', currency: currencyIso}).formatToParts(0)
    const currencyPart = parts.find((part) => part.type === 'currency')
    return currencyPart ? currencyPart.value : currencyIso
  } catch {
    return currencyIso
  }
}
